import { Tooltip } from '@hn-ui/tooltips';
import { styled } from 'styled-components';
import { Heading, HeadingTxt, InfoBtn } from './Common';
import { statusArr, statusColorsMap } from './utils';
import { PieChart } from './PieChart';
import { HelpIcon } from '../Common/Icons';
import {
  useWeeklyDashboardAnalytics,
  WeeklyDashboardEvents,
} from './hooks/useWeeklyDashboardAnalytics';

const SummaryContainer = styled.div`
  background: #ffffff;
  box-shadow: 0px 4px 20px rgb(0 0 0 / 7%);
  border-radius: 12px;
  position: relative;
`;

const Flex = styled.div`
  display: flex;
  flex-direction: column;
`;

const Chart = styled.div`
  display: flex;
`;

const PositionedChart = styled(Chart)`
  margin-top: ${15}px;
  min-height: ${24 + 100 + 8 + 12}px;
`;

const ChartTxt = styled.p`
  ${HeadingTxt}
  line-height: 12px;
  text-align: center;
  margin: 0;
  width: 100%;
`;

const SetPieChart = styled(PieChart)`
  margin: 0 auto;
`;

const SetLegendPieChart = styled(PieChart)`
  margin: 3px 2px 0 0;
`;

const ChartColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 16px;
  margin-right: 15px;
  &:last-child {
    margin-right: 0;
  }
`;

const TipContainer = styled.div`
  width: 100%;
  margin-bottom: 8px;
  bottom: 0;
  margin-top: auto;
`;

const ChartBar = styled.div`
  width: 100%;
  height: ${({ percent = 1 }) => percent}px;
  bottom: 0;
  background-color: ${({ bg }) => bg};
  min-height: 1px;
  border-radius: 2px;
`;

const ChartBarValue = styled.p`
  /* margin-top: auto; */
  font-family: var(--main-font);
  font-size: 16px;
  line-height: 24px;
  color: #242527;
  margin-bottom: 0;
  width: 100%;
  text-align: center;
`;

const SummarySubContainer = styled.div`
  display: grid;
  grid-template-columns: 42% 58%;
`;

const PositionedInfoBtn = styled(InfoBtn)`
  margin: 0 4px;
  position: relative;
  top: 4px;
`;

const Width33 = styled.div`
  width: 33.3%;
  float: left;
  padding-right: 20px;
`;

const FlexContainer = styled.div`
  display: flex;
`;

const SummaryColumn = styled.section`
  padding: 32px 30px 32px 31px;
  position: relative;
  &:first-child {
    border-right: 1px solid #cce8f9;
  }
`;

const SummarySectionHeading = styled.h2`
  font-family: var(--main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: #777b8b;
  margin-top: 0;
  margin-bottom: 34px;
`;

const Number = styled.p`
  font-family: var(--main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 36px;
  line-height: 48px;
  letter-spacing: -0.6px;
  color: #2ba1e6;
  display: inline-block;
  vertical-align: top;
`;

const ChartLegend = styled.div`
  margin-left: 29px;
  display: flex;
  flex-direction: column;
  align-items: end;
  margin-top: auto;
`;

const ChartLegendRow = styled.div`
  display: flex;
  margin-bottom: 7px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ChartLegendTitle = styled.h3`
  font-family: var(--main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  line-height: 16px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #242527;
  margin: 0;
`;

const ScHelpIcon = styled(HelpIcon)`
  vertical-align: middle;
  margin: 0 4px;
`;

const getStudentsNamesByIds = (ids, arr) => {
  const result = ids?.map((id) => [arr[id]?.full_name, <br key={id} />]).flat();
  return result?.length ? result : '';
};

const Summary = ({ className, data, days, isCurrentWeek, toggleModal }) => {
  const { weekly_plan_completed: weeklyPlanCompleted } = data;
  const activeStudentsList = getStudentsNamesByIds(
    data.active_students_count,
    data.students
  );
  const currentlyOnlineList = getStudentsNamesByIds(
    data.online_students_count,
    data.students
  );
  const timeTargetCompletedList = getStudentsNamesByIds(
    data.weekly_target_completion.completed,
    data.students
  );
  const taskTargetCompletedList = getStudentsNamesByIds(
    weeklyPlanCompleted,
    data.students
  );

  const sendGaEvent = useWeeklyDashboardAnalytics();

  return (
    <SummaryContainer className={className}>
      <SummarySubContainer>
        <SummaryColumn>
          <SummarySectionHeading>
            Activity
            <Tooltip
              content={`Active students are students
                  who started at least one task
                  on a week.`}
              maxWidth='none'
            >
              <ScHelpIcon />
            </Tooltip>
          </SummarySectionHeading>
          <Width33>
            <div
              onMouseEnter={() =>
                sendGaEvent(WeeklyDashboardEvents.activeStudentsHover)
              }
            >
              <Heading>
                {`Active
              students`}
              </Heading>
              <Tooltip
                placement='left'
                content={activeStudentsList}
                interactive={!!activeStudentsList}
                maxWidth='none'
              >
                <Number>{data.active_students_count.length}</Number>
              </Tooltip>
            </div>
            {isCurrentWeek && (
              <div
                onMouseEnter={() =>
                  sendGaEvent(WeeklyDashboardEvents.onlineStudentsHover)
                }
              >
                <Heading>
                  {`Currently
                online`}
                </Heading>
                <Tooltip
                  placement='left'
                  content={currentlyOnlineList}
                  interactive={!!currentlyOnlineList}
                  maxWidth='none'
                >
                  <Number>{data.online_students_count.length}</Number>
                </Tooltip>
              </div>
            )}
          </Width33>
          <Flex>
            <Heading>Active students by day</Heading>
            <PositionedChart
              onMouseEnter={() =>
                sendGaEvent(WeeklyDashboardEvents.studentsActivityDiagramHover)
              }
            >
              {days.map(({ day, abbr }) => {
                const active = data.active_students_by_days[day];
                const activeStudentsByDayList = getStudentsNamesByIds(
                  active,
                  data.students
                );
                return (
                  <ChartColumn key={day}>
                    <TipContainer>
                      <Tooltip
                        placement='left'
                        content={activeStudentsByDayList}
                        interactive={!!activeStudentsByDayList}
                        maxWidth='none'
                      >
                        <>
                          <ChartBarValue>
                            {active?.length > 0 ? active.length : ''}
                          </ChartBarValue>
                          <ChartBar
                            percent={
                              (active?.length /
                                Object.keys(data.students).length) *
                              100
                            }
                            bg='#2BA1E6'
                          />
                        </>
                      </Tooltip>
                    </TipContainer>
                    <ChartTxt>{abbr}</ChartTxt>
                  </ChartColumn>
                );
              })}
            </PositionedChart>
          </Flex>
        </SummaryColumn>
        <SummaryColumn>
          <SummarySectionHeading>
            Target Completion
            <PositionedInfoBtn
              aria-label='How target completion is calculated'
              onClick={toggleModal}
            />
          </SummarySectionHeading>
          <Width33>
            <div
              onMouseEnter={() =>
                sendGaEvent(WeeklyDashboardEvents.timeTargetCompletedHover)
              }
            >
              <Heading>
                {`Time target
              completed`}
              </Heading>
              <Tooltip
                placement='left'
                content={timeTargetCompletedList}
                interactive={!!timeTargetCompletedList}
                maxWidth='none'
              >
                <Number>
                  {data.weekly_target_completion.completed.length}
                </Number>
              </Tooltip>
            </div>
            <div
              onMouseEnter={() =>
                sendGaEvent(WeeklyDashboardEvents.taskTargetCompletedHover)
              }
            >
              <Heading>
                {`Task target
              completed`}
              </Heading>
              <Tooltip
                placement='left'
                content={taskTargetCompletedList}
                interactive={!!taskTargetCompletedList}
                maxWidth='none'
              >
                <Number>{weeklyPlanCompleted.length}</Number>
              </Tooltip>
            </div>
          </Width33>
          <div>
            <Heading>Time target completion</Heading>
            <FlexContainer
              onMouseEnter={() =>
                sendGaEvent(WeeklyDashboardEvents.targetsDiagramHover)
              }
            >
              <PositionedChart>
                {statusArr.map((field) => {
                  const { color, fill, title } = statusColorsMap.get(field);
                  const completion = data.weekly_target_completion[field];
                  const timeTargetCompletionList = getStudentsNamesByIds(
                    completion,
                    data.students
                  );
                  return (
                    <ChartColumn key={title}>
                      <TipContainer>
                        <Tooltip
                          placement='left'
                          content={timeTargetCompletionList}
                          interactive={!!timeTargetCompletionList}
                          maxWidth='none'
                        >
                          <>
                            <ChartBarValue>
                              {completion?.length > 0 ? completion.length : ''}
                            </ChartBarValue>
                            <ChartBar
                              percent={
                                (completion?.length /
                                  Object.keys(data.students).length) *
                                100
                              }
                              bg={color}
                            />
                          </>
                        </Tooltip>
                      </TipContainer>
                      <SetPieChart color={color} width={12} fill={fill} />
                    </ChartColumn>
                  );
                })}
              </PositionedChart>
              <ChartLegend>
                {statusArr.map((field) => {
                  const { color, fill, title } = statusColorsMap.get(field);

                  return (
                    <ChartLegendRow key={title}>
                      <SetLegendPieChart color={color} width={10} fill={fill} />
                      <ChartLegendTitle>{title}</ChartLegendTitle>
                    </ChartLegendRow>
                  );
                })}
              </ChartLegend>
            </FlexContainer>
          </div>
        </SummaryColumn>
      </SummarySubContainer>
    </SummaryContainer>
  );
};
export default Summary;
